import { useEffect, useMemo, useState } from "react";

import {
  type CloudCaptureStatus,
  type CloudSnapshotSummary,
  type CloudSnapshotWarnings,
} from "@board-preserve/contracts";

export type BackupWarningReportState = {
  error: string | null;
  report: CloudSnapshotWarnings | null;
  state: "idle" | "loading" | "ready" | "failed";
};

type FailedCapture = {
  message: string | null;
  status: Pick<
    CloudCaptureStatus,
    "createdAt" | "errorCode" | "jobId" | "progress" | "snapshotStatus"
  >;
};

type BackupHistoryPanelProps = {
  canCompare: boolean;
  canRestore: boolean;
  downloadMessage: string | null;
  downloadingSnapshotId: string | null;
  deleteError: string | null;
  deletingSnapshotId: string | null;
  error: string | null;
  failedCapture: FailedCapture | null;
  loading: boolean;
  onCompare: (snapshotId: string) => void;
  onCreateRecoveryBoard: (snapshotId: string) => void;
  onDelete: (snapshot: CloudSnapshotSummary, confirmation: string) => void;
  onDownload: (snapshot: CloudSnapshotSummary) => void;
  onOpenPlans: () => void;
  onRecoverItem: (snapshotId: string) => void;
  onRestoreOriginal: (snapshotId: string) => void;
  onRetryFailedCapture: () => void;
  onRetryHistory: () => void;
  onSelect: (snapshotId: string) => void;
  onWarningLoad: (snapshotId: string) => void;
  selectedSnapshotId: string | null;
  snapshots: CloudSnapshotSummary[];
  warningReports: Record<string, BackupWarningReportState>;
};

const COLLAPSED_BACKUP_COUNT = 4;

export function isBackupDeleteConfirmed(value: string): boolean {
  return value === "Delete";
}

function formatBytes(bytes: number): string {
  if (bytes < 1_000) return `${bytes} B`;
  if (bytes < 1_000_000) return `${(bytes / 1_000).toFixed(1)} KB`;
  return `${(bytes / 1_000_000).toFixed(1)} MB`;
}

function calendarDayOffset(date: Date, now: Date): number {
  const day = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((today.getTime() - day.getTime()) / 86_400_000);
}

function formatBackupTime(value: string, now = new Date()): string {
  const date = new Date(value);
  const time = new Intl.DateTimeFormat(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
  const offset = calendarDayOffset(date, now);
  if (offset === 0) return `Today, ${time}`;
  if (offset === 1) return `Yesterday, ${time}`;
  return new Intl.DateTimeFormat(undefined, {
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    month: "short",
  }).format(date);
}

function formatExpiry(value: string): string {
  return new Intl.DateTimeFormat(undefined, {
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    month: "short",
  }).format(new Date(value));
}

function failedCaptureExplanation(errorCode: string | null): {
  description: string;
  title: string;
} {
  const known: Record<string, { description: string; title: string }> = {
    capture_dispatch_failed: {
      description:
        "The backup could not be handed to a secure worker. Nothing was stored. Try again now; scheduled backups will also retry on their next run.",
      title: "The backup could not start",
    },
    capture_job_attempts_exhausted: {
      description:
        "Trello or the backup worker did not respond after several safe retries. The incomplete attempt was discarded and no storage was used.",
      title: "The backup stopped after repeated retries",
    },
  };
  return (
    (errorCode ? known[errorCode] : undefined) ?? {
      description:
        "The backup did not finish, so the incomplete attempt was discarded. No downloadable archive was stored and earlier backups are unaffected.",
      title: "The backup could not be completed",
    }
  );
}

function captureStageRows(status: FailedCapture["status"]): Array<{
  label: string;
  state: "done" | "stopped" | "not_run";
}> {
  const stageIndex = {
    building_archive: 2,
    capturing_attachments: 1,
    capturing_structure: 0,
    complete: 3,
    complete_with_warnings: 3,
    failed: 0,
    pending: -1,
  }[status.snapshotStatus];
  const failedIndex = Math.max(0, stageIndex);
  return ["Board content captured", "Attachments copied", "Archive built"].map(
    (label, index) => ({
      label,
      state:
        index < failedIndex
          ? "done"
          : index === failedIndex
            ? "stopped"
            : "not_run",
    }),
  );
}

export function BackupHistoryPanel({
  canCompare,
  canRestore,
  downloadMessage,
  downloadingSnapshotId,
  deleteError,
  deletingSnapshotId,
  error,
  failedCapture,
  loading,
  onCompare,
  onCreateRecoveryBoard,
  onDelete,
  onDownload,
  onOpenPlans,
  onRecoverItem,
  onRestoreOriginal,
  onRetryFailedCapture,
  onRetryHistory,
  onSelect,
  onWarningLoad,
  selectedSnapshotId,
  snapshots,
  warningReports,
}: BackupHistoryPanelProps) {
  const [expandedWarningId, setExpandedWarningId] = useState<string | null>(
    null,
  );
  const [showOlder, setShowOlder] = useState(false);
  const [showFailure, setShowFailure] = useState(false);
  const [deleteCandidateId, setDeleteCandidateId] = useState<string | null>(
    null,
  );
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const sortedSnapshots = useMemo(
    () =>
      [...snapshots].sort(
        (left, right) =>
          new Date(right.capturedAt).getTime() -
          new Date(left.capturedAt).getTime(),
      ),
    [snapshots],
  );
  const visibleSnapshots = showOlder
    ? sortedSnapshots
    : sortedSnapshots.slice(0, COLLAPSED_BACKUP_COUNT);
  const hiddenCount = Math.max(
    0,
    sortedSnapshots.length - COLLAPSED_BACKUP_COUNT,
  );

  useEffect(() => {
    if (
      deleteCandidateId !== null &&
      !snapshots.some(({ id }) => id === deleteCandidateId)
    ) {
      setDeleteCandidateId(null);
      setDeleteConfirmation("");
    }
  }, [deleteCandidateId, snapshots]);

  if (showFailure && failedCapture) {
    const explanation = failedCaptureExplanation(
      failedCapture.status.errorCode,
    );
    const percent = failedCapture.status.progress.total
      ? Math.round(
          (failedCapture.status.progress.completed /
            failedCapture.status.progress.total) *
            100,
        )
      : 0;
    return (
      <section
        className="backup-failure-detail"
        aria-labelledby="failure-title"
      >
        <button
          className="task-back-link"
          onClick={() => setShowFailure(false)}
          type="button"
        >
          ‹ Backups
        </button>
        <h3 id="failure-title">This backup failed</h3>
        <p className="backup-failure-meta">
          {formatBackupTime(failedCapture.status.createdAt)} · stopped at{" "}
          {percent}%
        </p>
        <div className="backup-failure-callout" role="alert">
          <strong>{explanation.title}</strong>
          <p>{explanation.description}</p>
        </div>
        <ol className="backup-stage-results">
          {captureStageRows(failedCapture.status).map((stage) => (
            <li key={stage.label}>
              <span
                className={`backup-stage-state backup-stage-${stage.state}`}
              >
                {stage.state === "done"
                  ? "Done"
                  : stage.state === "stopped"
                    ? "Stopped"
                    : "Not run"}
              </span>
              {stage.label}
            </li>
          ))}
        </ol>
        <section className="backup-failure-next">
          <strong>What happens next</strong>
          <p>
            Nothing from this attempt was kept. Earlier backups are unchanged,
            and the next scheduled backup will run normally.
          </p>
        </section>
        <div className="backup-failure-actions">
          <button onClick={onRetryFailedCapture} type="button">
            Try again now
          </button>
          <details>
            <summary>Technical details</summary>
            <code>{failedCapture.status.errorCode ?? "capture_failed"}</code>
          </details>
        </div>
        <p className="backup-support-reference">
          If it keeps failing, quote reference BP-
          {failedCapture.status.jobId.slice(0, 4).toUpperCase()}-
          {failedCapture.status.jobId.slice(-4).toUpperCase()} to support.
        </p>
      </section>
    );
  }

  return (
    <section className="backup-history-list" aria-label="Retained backups">
      {loading && snapshots.length === 0 ? (
        <div className="backup-history-skeleton" aria-label="Loading backups">
          <span />
          <span />
          <span />
        </div>
      ) : null}
      {error ? (
        <div className="backup-history-error" role="alert">
          <span>{error}</span>
          <button onClick={onRetryHistory} type="button">
            Try again
          </button>
        </div>
      ) : null}
      {!loading && !error && snapshots.length === 0 && !failedCapture ? (
        <div className="backup-history-empty">
          <strong>No backups yet</strong>
          <p>Create a managed backup to see it here.</p>
        </div>
      ) : null}

      <div className="backup-history-rows">
        {visibleSnapshots.map((snapshot) => {
          const selected = snapshot.id === selectedSnapshotId;
          const warningOpen = expandedWarningId === snapshot.id;
          const warningReport = warningReports[snapshot.id] ?? {
            error: null,
            report: null,
            state: "idle" as const,
          };
          const hasWarnings = snapshot.warningCount > 0;
          return (
            <article
              className={`backup-history-row${selected ? " is-selected" : ""}${hasWarnings ? " has-warnings" : ""}`}
              key={snapshot.id}
            >
              <label className="backup-row-summary">
                <input
                  aria-label={`Select backup captured ${new Date(snapshot.capturedAt).toLocaleString()}`}
                  checked={selected}
                  name="restore-snapshot"
                  onChange={() => onSelect(snapshot.id)}
                  type="radio"
                />
                <span className="backup-state-dot" aria-hidden="true" />
                <span className="backup-row-copy">
                  <strong>{formatBackupTime(snapshot.capturedAt)}</strong>
                  <span>
                    {snapshot.capturePurpose === "restore_safety"
                      ? "Safety backup"
                      : "Managed backup"}
                    {" · "}
                    {formatBytes(snapshot.totalBytes)}
                    {" · expires "}
                    {formatExpiry(snapshot.expiresAt)}
                  </span>
                  <span>
                    {snapshot.attachmentOutcomes.copied} file
                    {snapshot.attachmentOutcomes.copied === 1 ? "" : "s"} copied
                    {hasWarnings
                      ? ` · ${snapshot.warningCount} warning${snapshot.warningCount === 1 ? "" : "s"}`
                      : ""}
                  </span>
                </span>
              </label>
              <div className="backup-row-leading-action">
                {hasWarnings ? (
                  <button
                    aria-expanded={warningOpen}
                    onClick={() => {
                      const next = warningOpen ? null : snapshot.id;
                      setExpandedWarningId(next);
                      if (next && warningReport.state === "idle") {
                        onWarningLoad(snapshot.id);
                      }
                    }}
                    type="button"
                  >
                    {warningOpen ? "Hide warning" : "See warning"}
                  </button>
                ) : selected ? (
                  <span className="backup-selected-label">Selected</span>
                ) : null}
              </div>

              {selected ? (
                <div className="backup-row-actions">
                  <button
                    className="primary-action"
                    disabled={downloadingSnapshotId === snapshot.id}
                    onClick={() => onDownload(snapshot)}
                    type="button"
                  >
                    {downloadingSnapshotId === snapshot.id
                      ? "Downloading…"
                      : "Download ZIP"}
                  </button>
                  <button
                    onClick={() =>
                      canRestore
                        ? onCreateRecoveryBoard(snapshot.id)
                        : onOpenPlans()
                    }
                    type="button"
                  >
                    Create a recovery board
                  </button>
                  <button
                    onClick={() =>
                      canRestore ? onRecoverItem(snapshot.id) : onOpenPlans()
                    }
                    type="button"
                  >
                    Recover one card or list
                  </button>
                  <button
                    onClick={() =>
                      canCompare ? onCompare(snapshot.id) : onOpenPlans()
                    }
                    type="button"
                  >
                    Compare with…
                  </button>
                  <button
                    className="backup-original-restore"
                    onClick={() =>
                      canRestore
                        ? onRestoreOriginal(snapshot.id)
                        : onOpenPlans()
                    }
                    type="button"
                  >
                    Restore onto this board · advanced
                  </button>
                  <button
                    className="backup-delete-action"
                    onClick={() => {
                      setDeleteCandidateId(snapshot.id);
                      setDeleteConfirmation("");
                    }}
                    type="button"
                  >
                    Delete backup
                  </button>
                </div>
              ) : null}

              {selected && deleteCandidateId === snapshot.id ? (
                <section
                  aria-labelledby={`delete-backup-${snapshot.id}`}
                  className="backup-delete-confirmation"
                >
                  <div>
                    <h4 id={`delete-backup-${snapshot.id}`}>
                      Delete this backup permanently?
                    </h4>
                    <p>
                      Its archive, copied attachments, and backup record will be
                      removed. This cannot be undone. Deleting a Trial backup
                      does not reset the one-time Trial.
                    </p>
                  </div>
                  <label>
                    Type <strong>Delete</strong> to confirm
                    <input
                      autoComplete="off"
                      onChange={(event) =>
                        setDeleteConfirmation(event.currentTarget.value)
                      }
                      spellCheck={false}
                      type="text"
                      value={deleteConfirmation}
                    />
                  </label>
                  {deleteError ? (
                    <p className="backup-delete-error" role="alert">
                      {deleteError}
                    </p>
                  ) : null}
                  <div className="backup-delete-buttons">
                    <button
                      className="secondary-action"
                      disabled={deletingSnapshotId === snapshot.id}
                      onClick={() => {
                        setDeleteCandidateId(null);
                        setDeleteConfirmation("");
                      }}
                      type="button"
                    >
                      Cancel
                    </button>
                    <button
                      className="danger-action"
                      disabled={
                        !isBackupDeleteConfirmed(deleteConfirmation) ||
                        deletingSnapshotId === snapshot.id
                      }
                      onClick={() => onDelete(snapshot, deleteConfirmation)}
                      type="button"
                    >
                      {deletingSnapshotId === snapshot.id
                        ? "Deleting…"
                        : "Delete permanently"}
                    </button>
                  </div>
                </section>
              ) : null}

              {hasWarnings && warningOpen ? (
                <div className="backup-warning-detail">
                  {warningReport.state === "loading" ? (
                    <p>Loading warning details…</p>
                  ) : null}
                  {warningReport.state === "failed" ? (
                    <div role="alert">
                      <p>{warningReport.error}</p>
                      <button
                        onClick={() => onWarningLoad(snapshot.id)}
                        type="button"
                      >
                        Try again
                      </button>
                    </div>
                  ) : null}
                  {warningReport.report?.warnings.map((warning) => (
                    <section key={`${warning.objectType}:${warning.item.id}`}>
                      <strong>{warning.item.name} could not be copied</strong>
                      <p>
                        On “{warning.card.name}”. {warning.message}
                      </p>
                    </section>
                  ))}
                  <details>
                    <summary>Technical details</summary>
                    <ul>
                      {warningReport.report?.warnings.map((warning) => (
                        <li key={warning.code}>
                          <code>{warning.code}</code>
                        </li>
                      ))}
                    </ul>
                  </details>
                </div>
              ) : null}
            </article>
          );
        })}

        {failedCapture ? (
          <article className="backup-history-row backup-history-row-failed">
            <div className="backup-row-summary">
              <span className="backup-state-dot" aria-hidden="true" />
              <span className="backup-row-copy">
                <strong>
                  {formatBackupTime(failedCapture.status.createdAt)}
                </strong>
                <span>Failed · nothing was stored</span>
              </span>
            </div>
            <div className="backup-row-leading-action">
              <button onClick={() => setShowFailure(true)} type="button">
                Why it failed
              </button>
            </div>
          </article>
        ) : null}
      </div>

      {hiddenCount > 0 ? (
        <button
          className="backup-show-older"
          onClick={() => setShowOlder((current) => !current)}
          type="button"
        >
          {showOlder ? "Show newest 4" : `Show ${hiddenCount} older backups`}
        </button>
      ) : null}
      {downloadMessage ? (
        <p className="backup-download-message" role="status">
          {downloadMessage}
        </p>
      ) : null}
    </section>
  );
}
